/** Small shared pieces of the counter screens. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { asApiError, errorMessageKey } from '@macksons/web-client';
import { uuidv7 } from '../offline/uuidv7';
import { useConnectivity, useLocale, useT } from '../hooks';
import { CheckIcon, CrossIcon } from './icons';

/** Localised message for anything an API call threw (problem `code` → `errors.<code>`). */
export function useErrorText(): (error: unknown) => string {
  const t = useT();
  const locale = useLocale();
  return useCallback((error: unknown) => t(errorMessageKey(asApiError(error).code, locale)), [t, locale]);
}

/** Message for a stored problem code (scan queue items). */
export function useProblemText(): (code: string) => string {
  const t = useT();
  const locale = useLocale();
  return useCallback((code: string) => t(errorMessageKey(code, locale)), [t, locale]);
}

export const useOnline = (): boolean => useConnectivity().online;

/** True when the OS asks for reduced motion (no animation, no haptics). */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Short confirmation haptic for a counter result. Feature-detected (`navigator.vibrate`,
 * absent on iOS Safari and desktop) and suppressed when the operator asked for reduced
 * motion. `tone` picks a pattern: a single pulse for success, a double pulse for failure.
 */
export function vibrateFor(tone: 'ok' | 'error'): void {
  if (prefersReducedMotion()) return;
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }) : undefined;
  if (typeof nav?.vibrate !== 'function') return;
  try {
    nav.vibrate(tone === 'error' ? [40, 60, 40] : 30);
  } catch {
    // Vibration can throw if the document is not focused; a missing buzz is harmless.
  }
}

/** Shown in place of actions that need the API (Req 28.4). */
export function OnlineOnlyNotice() {
  const t = useT();
  return (
    <p className="notice notice--warning" data-testid="online-only">
      {t('operator.offline.onlineOnly')}
    </p>
  );
}

/**
 * Idempotency-Key per logical request: the same payload (`signature`) reuses its key, so
 * retrying after a lost response returns the original result instead of posting twice;
 * a different payload, or `reset()` after a completed action, gets a fresh UUIDv7.
 */
export function useIdempotencyKey(): { keyFor(signature: string): string; reset(): void } {
  const ref = useRef<{ signature: string; key: string } | undefined>(undefined);
  return {
    keyFor(signature) {
      if (ref.current?.signature !== signature) ref.current = { signature, key: uuidv7(Date.now()) };
      return ref.current.key;
    },
    reset() {
      ref.current = undefined;
    },
  };
}

export type Feedback = { tone: 'ok' | 'info' | 'error'; text: string } | undefined;

/**
 * Always-mounted live region for the outcome of an action: errors are assertive
 * (`role="alert"`), results polite (`role="status"`).
 */
export function FeedbackRegion({ feedback, testId }: { feedback: Feedback; testId?: string }) {
  return (
    <>
      <div role="alert" className={feedback?.tone === 'error' ? 'notice notice--error' : 'visually-hidden'} data-testid={testId && `${testId}-error`}>
        {feedback?.tone === 'error' ? feedback.text : ''}
      </div>
      <div
        role="status"
        aria-live="polite"
        className={feedback && feedback.tone !== 'error' ? `notice notice--${feedback.tone}` : 'visually-hidden'}
        data-testid={testId && `${testId}-status`}
      >
        {feedback && feedback.tone !== 'error' ? feedback.text : ''}
      </div>
    </>
  );
}

/**
 * Unmistakable result for a completed counter action: a big icon, the message, and (for a
 * successful points change) the points in large tabular numerals. Two always-mounted live
 * regions — a failure is assertive (`role="alert"`), a success/info polite (`role="status"`)
 * — so a screen reader announces the outcome; a matching haptic fires once per distinct
 * result (feature-detected, reduced-motion-aware). The `${testId}-error` / `${testId}-status`
 * hooks sit on the message text (same contract as {@link FeedbackRegion}); the large points
 * figure is a decorative companion marked aria-hidden so it is not read or counted twice.
 */
export function ResultCard({ feedback, points, testId }: { feedback: Feedback; points?: string; testId?: string }) {
  const seen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!feedback) {
      seen.current = undefined;
      return;
    }
    const sig = `${feedback.tone}:${feedback.text}`;
    if (seen.current === sig) return;
    seen.current = sig;
    if (feedback.tone === 'ok') vibrateFor('ok');
    else if (feedback.tone === 'error') vibrateFor('error');
  }, [feedback]);

  const isError = feedback?.tone === 'error';
  const isResult = feedback && feedback.tone !== 'error';
  return (
    <>
      <div role="alert" className={isError ? 'result result--error' : 'visually-hidden'}>
        {isError && feedback && (
          <>
            <CrossIcon className="result__icon" />
            <div className="result__body">
              <span className="result__text" data-testid={testId && `${testId}-error`}>
                {feedback.text}
              </span>
            </div>
          </>
        )}
      </div>
      <div role="status" aria-live="polite" className={isResult ? `result result--${feedback.tone}` : 'visually-hidden'}>
        {isResult && feedback && (
          <>
            <CheckIcon className="result__icon" />
            <div className="result__body">
              {points !== undefined && feedback.tone === 'ok' && (
                <span className="result__points" aria-hidden="true">
                  {points}
                </span>
              )}
              <span className="result__text" data-testid={testId && `${testId}-status`}>
                {feedback.text}
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Current time, re-rendering every `intervalMs` while mounted. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** `mm:ss` for a non-negative duration. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
